import { QueryClient } from "@tanstack/react-query";
import { isApiError } from "@/lib/api-error";

/**
 * TanStack Query defaults, tuned for a phone on mobile data in a workshop
 * (spec §1.1, §11.1, §13).
 *
 * Every value below is a deliberate departure from the library defaults, and
 * each one is a decision about the owner's signal, not about developer
 * convenience.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        /**
         * 60s. The library default is 0, which refetches on every mount — so
         * navigating Home → Invoices → Home re-downloads the dashboard over a
         * 3G link. Nothing in this app changes without this client causing it
         * (two users, one of whom is usually the person holding the phone), so
         * a minute of staleness is invisible and saves most of the requests.
         */
        staleTime: 60_000,

        /**
         * 30 minutes in cache. Long, on purpose: the owner's usage pattern is
         * to open the app, do one thing, lock the phone, and come back twenty
         * minutes later. Keeping the data means the second visit paints
         * instantly instead of showing a spinner in a spot with no signal.
         */
        gcTime: 30 * 60_000,

        /**
         * ONE retry, and never for an error the server has already decided.
         *
         * Retrying a 400/401/403/404 is pointless — the answer will not change
         * — and retrying a 429 is actively harmful, since the auth limiter
         * (apps/api/src/middleware/rate-limit.ts) counts every attempt. A
         * transport failure IS worth one retry: a workshop wi-fi handover drops
         * a single request routinely.
         */
        retry: (failureCount, error) => {
          if (failureCount >= 1) return false;
          if (isApiError(error)) {
            if (error.isNetworkFailure) return true;
            if (error.status >= 400 && error.status < 500) return false;
            return true; // 5xx — worth one more try
          }
          return false;
        },
        retryDelay: 1_500,

        /**
         * Refetch when the phone comes back from the lock screen, so the owner
         * never acts on a figure from before the tunnel. This is the one
         * automatic refetch that earns its bandwidth.
         */
        refetchOnWindowFocus: true,

        /**
         * And when the network returns — the counterpart to the offline draft
         * story in §11.1.
         */
        refetchOnReconnect: true,

        /**
         * `true`, NOT `false`. The comment that used to sit here was wrong about
         * the library, and the mistake cost a real bug.
         *
         * The reasoning was: "`staleTime` already decides this, so `true` would
         * refetch on every navigation regardless of staleness." That is not what
         * `true` does — react-query gates it behind `isStale()`, so `true`
         * refetches on mount ONLY when the data is stale or invalidated, which
         * preserves every byte `staleTime` was meant to save.
         *
         * What `false` did was disable the check entirely, including for
         * INVALIDATED data. `invalidateQueries` defaults to
         * `refetchType: "active"`, so invalidating a list that is not currently
         * mounted only marks it — the refetch is meant to happen when it mounts.
         * With `false` that never happened.
         *
         * The user-visible failure: save an invoice on /invoices/new, tap
         * Invoices, and the register shows the list from BEFORE the save. To the
         * owner that reads as "it didn't save", and the natural response is to
         * type the bill again. §7 makes the server the authority on what exists;
         * a register that silently shows a stale answer is the client
         * contradicting it on the one screen he checks.
         */
        refetchOnMount: true,

        /**
         * Keep the previous page's rows on screen while the next page loads,
         * instead of flashing a skeleton. On a slow link the flash is the whole
         * experience.
         */
        placeholderData: <T,>(previous: T) => previous,
      },

      mutations: {
        /**
         * NEVER retry a mutation automatically.
         *
         * A retried `POST /invoices` is a second invoice, and §5 numbering is
         * gap-free and irreversible — the duplicate would consume a number that
         * can then only be CANCELLED, never removed (§4: invoices are never
         * hard-deleted). A failed save is retried by the owner pressing the
         * button again, or by the Phase 5 Dexie sync, which is idempotent
         * because it carries a client UUID (§11.1). Not by this library.
         */
        retry: false,
      },
    },
  });
}
